import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Capacitor } from '@capacitor/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { AppUpdateDialogComponent } from '../componentsCustom/app-update-dialog/app-update-dialog.component';
import { extractApiError } from '../core/utils/api-error';
import { isChunkLoadError } from '../core/utils/chunk-load-error';

const UPGRADE_FALLBACK_MESSAGE = 'This version of the app is no longer supported. Please update to continue.';

// Long enough to be cheap, short enough that someone who leaves a tab open over lunch
// comes back to a current app.
const DEPLOY_CHECK_INTERVAL = 15 * 60 * 1000;

// Holds the URL a reload was meant to reach. sessionStorage rather than a field: the
// whole point is that it has to survive the reload it is guarding against.
const RECOVERY_KEY = 'app-update.chunk-recovery';

const RECOVERY_FAILED_MESSAGE = 'This page could not be loaded. Please close this tab and open the portal again.';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {

  private upgradeDialogOpen = false;

  private deployWatchStarted = false;
  private runningBuild = '';
  private lastEtag: string | null = null;
  private newBuildAnnounced = false;
  private pollSubscription?: Subscription;

  private chunkRecoveryStarted = false;

  constructor(private dialog: MatDialog, private snackBar: MatSnackBar, private router: Router) { }

  // Called from VersionInterceptor when the backend answers 426. A page load fires
  // several requests at once and every one of them comes back 426, so this has to be
  // safe to call repeatedly - only the first call opens a dialog.
  forceUpgrade(error: HttpErrorResponse): void {
    if (this.upgradeDialogOpen) {
      return;
    }
    this.upgradeDialogOpen = true;

    this.dialog.open(AppUpdateDialogComponent, {
      disableClose: true,
      data: {
        // The backend sends a user-facing message in `detail`.
        message: extractApiError(error, UPGRADE_FALLBACK_MESSAGE)
      }
    });
  }

  // Cache headers make sure a page load gets the current build, but a tab left open for
  // days never asks again. This notices a deploy that happened underneath a live session,
  // and also one that happened before it, if the page itself was served from cache.
  startDeployWatch(): void {
    // Native serves index.html out of the binary, so the deployed and running builds are
    // the same file by definition and this could never fire. Being too old is the
    // backend's call there, and it says so with a 426.
    if (this.deployWatchStarted || Capacitor.isNativePlatform()) {
      return;
    }
    this.deployWatchStarted = true;

    this.runningBuild = this.buildIdOf(document);
    if (!this.runningBuild) {
      // Nothing recognisable to compare against, so every check would read as a new build.
      // Better to do nothing than to nag on every page load.
      console.log('could not identify the running build, skipping deploy watch');
      return;
    }

    this.pollSubscription = interval(DEPLOY_CHECK_INTERVAL).subscribe(() => this.checkForNewBuild());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState == 'visible') {
        this.checkForNewBuild();
      }
    });

    this.checkForNewBuild();
  }

  // The set of bundles index.html points at identifies the build. esbuild content hashes
  // the chunks on its own, and the entry points are hashed because angular.json sets
  // outputHashing on the development configuration too - deliberately, and load bearing:
  // without it main.js keeps its name across deploys, and most application code lands
  // only there, so a real change would read as the same build and nothing would fire.
  // Only build output counts - ScriptLoaderService appends third-party <script> tags at
  // runtime and ng serve adds its own, and neither says anything about which build is live.
  private buildIdOf(doc: Document): string {
    const refs = [
      ...Array.from(doc.querySelectorAll('script[src]')).map(el => el.getAttribute('src')),
      ...Array.from(doc.querySelectorAll('link[rel="modulepreload"][href]')).map(el => el.getAttribute('href')),
    ];

    return refs
      .map(ref => ref?.split('/').pop()?.split('?')[0] ?? '')
      // main-AIJTYSIB.js, polyfills-TW7W7OZB.js and chunk-H2BB6AX7.js in a build. The dev
      // server hashes the entry points the same way, it just emits no modulepreload links.
      .filter(name => /^(main|polyfills|styles|chunk)(-[A-Z0-9]+)?\.(js|css)$/.test(name))
      .sort()
      .join('|');
  }

  private async checkForNewBuild(): Promise<void> {
    if (this.newBuildAnnounced) {
      return;
    }

    try {
      // The deployed index.html, asked for by path rather than by name. Both resolve to
      // the same file with the same ETag, on the dev server and on the server alike, but
      // ng serve's HTML fallback logs a warning for anything that looks like an .html
      // file - and this runs on every tab focus, so it would never stop printing.
      const url = '/?t=' + Date.now();

      // index.html is 58kb on prod, so do not pull it down every quarter of an hour just
      // to find it unchanged. Its ETag moves whenever it is re-uploaded.
      if (this.lastEtag) {
        const head = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        if (head.ok && head.headers.get('etag') == this.lastEtag) {
          return;
        }
      }

      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }
      this.lastEtag = response.headers.get('etag');

      const deployed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const deployedBuild = this.buildIdOf(deployed);

      if (deployedBuild && deployedBuild != this.runningBuild) {
        this.announceNewBuild();
      }
    } catch (error) {
      // Offline, or index.html momentarily unavailable while a deploy is being uploaded.
      // Never surface this - the next check picks it up.
      console.log('could not check for a new build');
    }
  }

  private announceNewBuild(): void {
    this.newBuildAnnounced = true;
    this.pollSubscription?.unsubscribe();

    this.snackBar
      .open('A new version of the portal is available.', 'Reload', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'customSnackBar'
      })
      .onAction()
      .subscribe(() => window.location.reload());
  }

  // startDeployWatch only ever offers a reload, and a quarter of an hour late at that.
  // This is the other half: by the time a missing chunk actually breaks a navigation,
  // the router has already given up and left the screen exactly as it was, which reads
  // as a dead click. A full load of the route they asked for picks up the current build
  // and carries on - the session survives it, the token is in storage, not in memory.
  startChunkLoadRecovery(): void {
    // Native carries its chunks inside the binary, so they cannot go missing underneath
    // a running session and a reload would have nothing newer to fetch.
    if (this.chunkRecoveryStarted || Capacitor.isNativePlatform()) {
      return;
    }
    this.chunkRecoveryStarted = true;

    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        this.recoverFromMissingChunk(event);
      } else if (event instanceof NavigationEnd) {
        this.clearRecoveryMark(event.url);
      }
    });
  }

  private recoverFromMissingChunk(event: NavigationError): void {
    if (!isChunkLoadError(event.error)) {
      return;
    }

    // The other way an import fails is a lost connection, where reloading would trade a
    // running app for the browser's offline page.
    if (!navigator.onLine) {
      return;
    }

    // The chunk can be missing from the new build too - a deploy caught halfway, or a 404
    // the browser cached under the year-long immutable header hashed assets are served
    // with. Reloading again would fail identically, so only one attempt is ever made.
    if (sessionStorage.getItem(RECOVERY_KEY)) {
      this.snackBar.open(RECOVERY_FAILED_MESSAGE, 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'customSnackBar'
      });
      return;
    }
    sessionStorage.setItem(RECOVERY_KEY, event.url);

    // urlUpdateStrategy is 'deferred', so a failed navigation leaves the address bar on
    // the page they came from. event.url is where they were actually headed - after a
    // login that is '/', which is the redirect to their dashboard.
    window.location.assign(event.url);
  }

  // Dropped only once the navigation it was standing in for has actually succeeded, so a
  // build that is broken for everyone cannot bounce someone between reloads.
  private clearRecoveryMark(url: string): void {
    if (sessionStorage.getItem(RECOVERY_KEY) == url) {
      sessionStorage.removeItem(RECOVERY_KEY);
    }
  }

}
